// packages/charts/src/react/ChartRendererContext.tsx
//
// Which component draws an ECharts option, and whether the drawing is the
// static edition (plan 2026-10-24-delivery-review-and-the-static-renderer,
// Decision 2).
//
// The live surfaces draw through `<EChart>`: a canvas mounted in an effect
// after `requestAnimationFrame`, sized by a `ResizeObserver`. A server render
// runs no effect, so the same tree rendered to a string holds an empty box.
// The edition's static renderer therefore provides its own component here
// (`StaticChart` in `@verity/report-components`, an SVG string made during
// render) and the box each chart gets, measured from the block's place on its
// page rather than from the DOM.
//
// ONE SEAM, NOT A SECOND DISPATCH. `ChartFrame` and `FunnelExplorerChart`
// keep every decision they make today (route, empty state, receipt, refusal);
// they only ask this context who draws the option. A surface that provides
// nothing gets the live `EChart`, exactly as before.
import * as React from "react";
import type { EChartsOption } from "echarts";
import { EChart } from "./EChart";

/** The box a chart is drawn in, in CSS px. Known up front on the static
 *  edition (the block's layout on a bounded page); absent on a live surface,
 *  which measures. */
export type ChartBox = { widthPx: number; heightPx: number };

export type ChartRendererComponentProps = {
  option: EChartsOption;
  ariaLabel?: string;
  /** The canvas a caller computed. The live `EChart` ignores both and fills
   *  its parent; a static renderer needs them, because nothing measures. */
  widthPx?: number;
  heightPx?: number;
};

export type ChartRenderer = {
  Chart: React.ComponentType<ChartRendererComponentProps>;
  /** True on the static edition (the PDF and the HTML it is printed from):
   *  no controls, no selection, motion off. */
  isStatic: boolean;
  /** The current block's box, when the surface knows it. */
  box: ChartBox | null;
};

function LiveChart({ option, ariaLabel }: ChartRendererComponentProps): React.ReactElement {
  return <EChart option={option} ariaLabel={ariaLabel} />;
}

export const LIVE_CHART_RENDERER: ChartRenderer = { Chart: LiveChart, isStatic: false, box: null };

export const ChartRendererContext = React.createContext<ChartRenderer>(LIVE_CHART_RENDERER);

export function useChartRenderer(): ChartRenderer {
  return React.useContext(ChartRendererContext);
}

/** The block's box for everything below it, keeping the document's renderer. */
export function ChartBoxProvider({
  box,
  children,
}: {
  box: ChartBox | null;
  children: React.ReactNode;
}): React.ReactElement {
  const renderer = useChartRenderer();
  const value = React.useMemo(() => ({ ...renderer, box }), [renderer, box]);
  return <ChartRendererContext.Provider value={value}>{children}</ChartRendererContext.Provider>;
}
